import React, { useEffect, useState, useCallback } from "react";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Thumb, DateRange, ArchivedToggle, TabBar, Row } from "@/components/list";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Archive, ArchiveRestore, Trash2, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["In Progress", "Ready for Review", "Sent to Client", "Approved", "Needs Changes"];
const TABS = ["All", ...STATUSES];

const MyCreatives = () => {
  const { user } = useAuth();
  const isEditor = user?.role === "editor";
  const [creatives, setCreatives] = useState([]);
  const [tab, setTab] = useState("All");
  const [archived, setArchived] = useState(false);
  const [from, setFrom] = useState(""); const [to, setTo] = useState("");

  const load = useCallback(async () => {
    const params = { archived };
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    try { const c = await api.get("/creatives", { params }); setCreatives(c.data); }
    catch (e) { toast.error("Failed to load creatives"); }
  }, [archived, from, to]);
  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => { await api.put(`/creatives/${id}`, { status }); toast.success("Status updated"); load(); };
  const setArch = async (id, val) => { await api.put(`/creatives/${id}`, { archived: val }); toast.success(val ? "Archived" : "Unarchived"); load(); };
  const del = async (id) => { await api.delete(`/creatives/${id}`); toast.success("Deleted"); load(); };

  const counts = TABS.reduce((a, t) => { a[t] = t === "All" ? creatives.length : creatives.filter((c) => c.status === t).length; return a; }, {});
  const filtered = tab === "All" ? creatives : creatives.filter((c) => c.status === tab);

  return (
    <div className="space-y-6">
      <div><h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">My Creatives</h1>
        <p className="text-zinc-400 mt-1">Click a thumbnail to open its Google Drive link.</p></div>

      <div className="flex flex-wrap items-center gap-3 justify-between">
        <TabBar tabs={TABS} value={tab} onChange={setTab} counts={counts} testidPrefix="status-tab" />
        <div className="flex items-center gap-3">
          <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} />
          <ArchivedToggle archived={archived} onToggle={() => setArchived((a) => !a)} />
        </div>
      </div>

      <div className="space-y-3">
        {filtered.map((c) => (
          <Row key={c.id} testid={`mycreative-${c.id}`}>
            <Thumb src={(c.image_path || c.client_thumbnail) ? fileUrl(c.image_path || c.client_thumbnail) : null} href={c.google_drive_link} alt={c.title} />
            <div className="min-w-0 flex-1">
              <p className="font-semibold truncate">{c.title}</p>
              <p className="text-zinc-400 text-sm truncate">{c.client_name} • {c.type}</p>
              {c.feedback && c.feedback.length > 0 && (
                <p className="text-xs mt-0.5 flex items-center gap-1"><MessageSquare className="w-3 h-3 text-brand" />
                  <span className={c.feedback[0].decision === "Approve" ? "text-brand" : "text-red-400"}>{c.feedback[0].decision}</span>
                  {c.feedback[0].comment && <span className="text-zinc-500 truncate">— {c.feedback[0].comment}</span>}</p>
              )}
            </div>
            {isEditor ? (
              <StatusBadge status={c.status} />
            ) : (
              <Select value={c.status} onValueChange={(v) => updateStatus(c.id, v)}>
                <SelectTrigger data-testid={`creative-status-select-${c.id}`} className="w-[170px] bg-ink-900 border-ink-700 h-9 text-sm shrink-0"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-ink-800 border-ink-700 text-white">{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <button data-testid={`archive-creative-${c.id}`} onClick={() => setArch(c.id, !archived)} className="text-zinc-500 hover:text-brand shrink-0" title={archived ? "Unarchive" : "Archive"}>
              {archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}</button>
            {user?.role === "admin" && <button data-testid={`delete-creative-${c.id}`} onClick={() => del(c.id)} className="text-zinc-500 hover:text-red-400 shrink-0"><Trash2 className="w-4 h-4" /></button>}
          </Row>
        ))}
        {filtered.length === 0 && <p className="text-zinc-500 py-8 text-center">No creatives here.</p>}
      </div>
    </div>
  );
};

export default MyCreatives;
